import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import { FaEdit, FaTrash, FaEye } from 'react-icons/fa';
import Swal from 'sweetalert2';
import { projectAPI } from '../../utils/api';
import ProjectCreate from './ProjectCreate';
import DataTable from '../../components_shared/DataTable';
import TableToolbar from '../../components_shared/TableToolbar';

function ProjectsTable({ reloadKey = 0, onReload, onCreate }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState('');
  const [viewRow, setViewRow] = useState(null);
  const [editRow, setEditRow] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await projectAPI.getAll();
      setRows(Array.isArray(res.data) ? res.data : (res.data?.content || []));
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Error', text: err.message });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [reloadKey]);

  const handleDelete = async (row) => {
    const ok = await Swal.fire({
      title: 'Delete Project?',
      text: `"${row.name}" will be permanently removed.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, delete',
      cancelButtonText: 'Cancel',
      confirmButtonColor: '#e74c3c',
    });
    if (ok.isConfirmed) {
      try {
        await projectAPI.delete(row.id);
        load();
        onReload && onReload();
        Swal.fire({ icon: 'success', title: 'Deleted', toast: true, position: 'top-end', timer: 3000, showConfirmButton: false });
      } catch (err) {
        Swal.fire({ icon: 'error', title: 'Error', text: err?.response?.data?.error || err.message });
      }
    }
  };

  const exportCsv = async () => {
    try {
      const res = await projectAPI.exportCsv();
      const blob = new Blob([res.data], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url; a.download = 'projects_export.csv'; a.click(); URL.revokeObjectURL(url);
    } catch (err) { Swal.fire({ icon: 'error', title: 'Export failed', text: err.message }); }
  };

  const importCsv = async (csv) => {
    const res = await projectAPI.importCsv(csv);
    await load();
    return res.data;
  };

  const downloadSample = async () => {
    try {
      const res = await projectAPI.sampleCsv();
      const blob = new Blob([res.data], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url; a.download = 'projects_sample.csv'; a.click(); URL.revokeObjectURL(url);
    } catch (err) { console.error(err); }
  };

  const columns = [
    { accessorKey: 'id', header: 'ID', size: 60 },
    {
      accessorKey: 'project_id',
      header: 'Code',
      cell: ({ getValue }) => <span style={{ fontFamily: 'monospace', fontSize: '0.85rem', color: '#64748b' }}>{getValue()}</span>,
    },
    {
      accessorKey: 'name',
      header: 'Project Name',
      cell: ({ getValue }) => <span style={{ fontWeight: 600, color: '#0f6fad' }}>{getValue()}</span>,
    },
    { accessorKey: 'project_year', header: 'Academic Year' },
    {
      accessorKey: 'is_active',
      header: 'Status',
      cell: ({ getValue }) => (
        <span className={`eduss-global-status-badge ${getValue() ? 'active' : 'inactive'}`}>
          {getValue() ? 'ACTIVE' : 'INACTIVE'}
        </span>
      ),
    },
    {
      id: 'actions',
      header: 'Actions',
      cell: ({ row }) => (
        <div className="eduss-global-action-buttons">
          <button className="eduss-global-action-btn view" onClick={() => setViewRow(row.original)}><FaEye /></button>
          <button className="eduss-global-action-btn edit" onClick={() => setEditRow(row.original)}><FaEdit /></button>
          <button className="eduss-global-action-btn delete" onClick={() => handleDelete(row.original)}><FaTrash /></button>
        </div>
      ),
    },
  ];

  const filteredData = rows.filter(r =>
    !filter ||
    r.name?.toLowerCase().includes(filter.toLowerCase()) ||
    r.project_id?.toLowerCase().includes(filter.toLowerCase()) ||
    r.project_year?.toLowerCase().includes(filter.toLowerCase())
  );

  return (
    <div className="premium-card" style={{ padding: '0 0 30px 0' }}>
      <TableToolbar
        title="Projects"
        subtitle="Manage distribution cycles and academic year initiatives"
        onAdd={onCreate}
        onExport={exportCsv}
        onImport={importCsv}
        onDownloadSample={downloadSample}
        onSearch={setFilter}
      />

      <div style={{ padding: '0 30px' }}>
        <DataTable
          data={filteredData}
          columns={columns}
          loading={loading}
          enableSelection={true}
        />
      </div>

      {viewRow && createPortal(
        <div className="eduss-global-modal-overlay" onClick={() => setViewRow(null)}>
          <div className="eduss-global-modal eduss-admin-form-modal" onClick={e => e.stopPropagation()}>
            <div className="eduss-global-modal-header">
              <h3>Project Details</h3>
              <button onClick={() => setViewRow(null)}>&times;</button>
            </div>
            <div className="eduss-global-modal-content">
              <table className="mini-info-table">
                <tbody>
                  <tr><th>ID</th><td>{viewRow.id}</td></tr>
                  <tr><th>Code</th><td>{viewRow.project_id}</td></tr>
                  <tr><th>Name</th><td>{viewRow.name}</td></tr>
                  <tr><th>Academic Year</th><td>{viewRow.project_year || '-'}</td></tr>
                  <tr><th>Status</th><td>
                    <span className={`eduss-global-status-badge ${viewRow.is_active ? 'active' : 'inactive'}`}>
                      {viewRow.is_active ? 'ACTIVE' : 'INACTIVE'}
                    </span>
                  </td></tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>,
        document.body
      )}

      {editRow && createPortal(
        <div className="eduss-global-modal-overlay" onClick={() => setEditRow(null)}>
          <div className="eduss-global-modal large eduss-admin-form-modal" onClick={e => e.stopPropagation()}>
            <div className="eduss-global-modal-header">
              <h3>Edit Project</h3>
              <button onClick={() => setEditRow(null)}>&times;</button>
            </div>
            <div className="eduss-global-modal-content">
              <ProjectCreate
                key={editRow?.id}
                project={editRow}
                onClose={() => setEditRow(null)}
                onSubmit={() => { load(); setEditRow(null); }}
              />
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}

ProjectsTable.propTypes = {
  reloadKey: PropTypes.number,
  onReload: PropTypes.func,
  onCreate: PropTypes.func,
};

export default ProjectsTable;
